import React, { useState, useEffect, useRef, useCallback } from 'react';
import { UserCircle } from 'lucide-react';

const NOTIFICATIONS = [
  "Sophie from London, UK just joined MDD",
  "James from Birmingham, UK just joined MDD",
  "Aisha from Manchester, UK just joined MDD",
  "Daniel from Leeds, UK just joined MDD",
  "Chloe from Bristol, UK just joined MDD",
  "Liam from Liverpool, UK just joined MDD",
  "Priya from Leicester, UK just joined MDD",
  "Jack from Nottingham, UK just joined MDD",
  "Emily from Sheffield, UK just joined MDD",
  "Marcus from Dudley, UK just joined MDD",
  "Hannah from Coventry, UK just joined MDD",
  "Ryan from Wolverhampton, UK just joined MDD",
  "Sarah from Oxford, UK just joined MDD",
  "Aaron from Reading, UK just joined MDD",
  "Fatima from Luton, UK just joined MDD",
  "Ben from Derby, UK just joined MDD",
  "Olivia from Cambridge, UK just joined MDD",
  "Kyle from Newcastle, UK just joined MDD",
  "Zoe from Brighton, UK just joined MDD",
  "Tariq from Slough, UK just joined MDD",
  "Michael from New York, USA just joined MDD",
  "Jessica from Los Angeles, USA just joined MDD",
  "David from Toronto, Canada just joined MDD",
  "Emma from Sydney, Australia just joined MDD",
  "Ahmed from Dubai, UAE just joined MDD",
  "Sofia from Madrid, Spain just joined MDD",
  "Lucas from Paris, France just joined MDD",
  "Isabella from Milan, Italy just joined MDD",
  "Noah from Berlin, Germany just joined MDD",
  "Mia from Amsterdam, Netherlands just joined MDD",
  "Ethan from Dublin, Ireland just joined MDD",
  "Grace from Cape Town, South Africa just joined MDD",
  "Oliver from Auckland, New Zealand just joined MDD",
  "Layla from Doha, Qatar just joined MDD",
  "Victor from Stockholm, Sweden just joined MDD",
  "Nina from Vienna, Austria just joined MDD",
  "Hassan from Riyadh, Saudi Arabia just joined MDD",
  "Elena from Athens, Greece just joined MDD",
  "Mateo from Mexico City, Mexico just joined MDD",
  "Ana from Lisbon, Portugal just joined MDD",
  "Chris from Chicago, USA just joined MDD",
  "Natalie from Vancouver, Canada just joined MDD",
  "Samir from Mumbai, India just joined MDD",
  "Wei from Singapore just joined MDD",
  "Pablo from Buenos Aires, Argentina just joined MDD",
  "Leila from Casablanca, Morocco just joined MDD",
  "Tom from Glasgow, UK just joined MDD",
  "Ruby from Cardiff, UK just joined MDD",
  "Andre from Johannesburg, South Africa just joined MDD",
  "Yasmin from Kuala Lumpur, Malaysia just joined MDD"
];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function parseName(text) {
  const match = text.match(/^(\w+)\s+from\s+(.+)\s+just joined MDD$/);
  if (match) return { name: match[1], location: match[2] };
  return { name: text, location: '' };
}

function randomDelay() {
  return 8000 + Math.random() * 4000; // 8-12 seconds
}

function randomVisible() {
  return 4000 + Math.random() * 2000; // 4-6 seconds
}

const INITIAL_DELAY = 5000;

export default function SignupNotification() {
  const [visible, setVisible] = useState(false);
  const [current, setCurrent] = useState(null);
  const queueRef = useRef([]);
  const indexRef = useRef(0);
  const timeoutRef = useRef(null);
  const isScrollingRef = useRef(false);
  const scrollTimerRef = useRef(null);

  const getQueue = useCallback(() => {
    if (indexRef.current >= queueRef.current.length) {
      queueRef.current = shuffle(NOTIFICATIONS);
      indexRef.current = 0;
    }
    const item = queueRef.current[indexRef.current];
    indexRef.current++;
    return item;
  }, []);

  const showNext = useCallback(() => {
    if (isScrollingRef.current) {
      timeoutRef.current = setTimeout(showNext, 2000);
      return;
    }
    const text = getQueue();
    setCurrent(parseName(text));
    setVisible(true);

    timeoutRef.current = setTimeout(() => {
      setVisible(false);
      timeoutRef.current = setTimeout(showNext, randomDelay());
    }, randomVisible());
  }, [getQueue]);

  useEffect(() => {
    queueRef.current = shuffle(NOTIFICATIONS);

    const handleScroll = () => {
      isScrollingRef.current = true;
      clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = setTimeout(() => {
        isScrollingRef.current = false;
      }, 1500);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('touchmove', handleScroll, { passive: true });

    timeoutRef.current = setTimeout(showNext, INITIAL_DELAY);

    return () => {
      clearTimeout(timeoutRef.current);
      clearTimeout(scrollTimerRef.current);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('touchmove', handleScroll);
    };
  }, [showNext]);

  if (!current) return null;

  const initial = current.name.charAt(0).toUpperCase();

  return (
    <div
      data-testid="signup-notification"
      className={`fixed bottom-20 left-4 z-50 transition-all duration-500 ease-out pointer-events-none
        sm:bottom-6 sm:left-6
        ${visible
          ? 'opacity-100 translate-y-0 translate-x-0'
          : 'opacity-0 translate-y-3 -translate-x-4'
        }`}
      style={{ maxWidth: '340px' }}
    >
      <div className="bg-white rounded-xl shadow-lg border border-border/60 px-4 py-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
          <span className="text-primary font-bold text-sm">{initial}</span>
        </div>
        <div className="min-w-0">
          <p className="text-sm text-dark font-medium leading-snug truncate" data-testid="signup-notification-text">
            <span className="font-semibold">{current.name}</span>
            <span className="text-text-secondary font-normal"> from {current.location}</span>
          </p>
          <p className="text-xs text-text-light">just joined MDD</p>
        </div>
        <div className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0 animate-pulse" />
      </div>
    </div>
  );
}
